import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE } from "../apiConfig";
import { useNavigate } from "react-router-dom";

export default function QuizPage() {
  const navigate = useNavigate();
  const user = sessionStorage.getItem("user");
  const quizCode = sessionStorage.getItem("quiz_code");

  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);

  const totalTime = 300; // 5 minutes
  const [timeLeft, setTimeLeft] = useState(totalTime);

  useEffect(() => {
    if (!user) navigate("/");
    if (!quizCode) navigate("/");

    const loadQuiz = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/getQuizByCode.php?quiz_code=${quizCode}`
        );

        if (res.data.status !== "success") {
          alert(res.data.message);
          navigate("/");
          return;
        }

        setQuiz(res.data);
      } catch (err) {
        alert("Failed to load quiz from server!");
      } finally {
        setLoading(false);
      }
    };

    loadQuiz();
  }, [user, quizCode, navigate]);

  const handleOptionChange = (qid, option) => {
    setAnswers((prev) => ({ ...prev, [qid]: option }));
  };

  const handleSubmit = () => {
    sessionStorage.setItem("quizData", JSON.stringify(quiz));
    sessionStorage.setItem("answers", JSON.stringify(answers));
    navigate("/result");
  };

  useEffect(() => {
    if (loading) return;

    if (timeLeft <= 0) {
      alert("Time up! Auto submitting...");
      handleSubmit();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, loading]); // eslint-disable-line

  const formattedTime = useMemo(() => {
    const mm = Math.floor(timeLeft / 60);
    const ss = String(timeLeft % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  }, [timeLeft]);

  const progressPercent = useMemo(() => {
    return Math.max(0, Math.min(100, (timeLeft / totalTime) * 100));
  }, [timeLeft]);

  const totalQuestions = quiz?.questions?.length || 0;

  const answeredCount = useMemo(() => {
    return Object.keys(answers).length;
  }, [answers]);

  const isLowTime = timeLeft <= 30;

  if (loading) {
    return (
      <div className="container py-5">
        <div className="d-flex align-items-center justify-content-center">
          <div className="spinner-border text-primary me-3" role="status" />
          <h5 className="mb-0">Loading Quiz...</h5>
        </div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger shadow-sm">
          ❌ No Quiz Found! Please contact admin.
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="card shadow-sm border-0 rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
            <div>
              <h3 className="fw-bold mb-1">{quiz.title}</h3>
              <p className="text-muted mb-0">
                Welcome, <span className="fw-semibold">{user}</span> 👋
              </p>
              <small className="text-secondary">
                Quiz Code: <b>{quizCode}</b>
              </small>
            </div>

            <div className="text-md-end">
              <span
                className={`badge rounded-pill px-3 py-2 ${
                  isLowTime ? "bg-danger" : "bg-success"
                }`}
                style={{ fontSize: "1rem" }}
              >
                ⏳ {formattedTime}
              </span>

              <div className="mt-2">
                <small className="text-muted">
                  Answered:{" "}
                  <span className="fw-bold">
                    {answeredCount}/{totalQuestions}
                  </span>
                </small>
              </div>
            </div>
          </div>

          <div className="progress mt-3" style={{ height: "10px" }}>
            <div
              className={`progress-bar ${
                isLowTime ? "bg-danger" : "bg-primary"
              }`}
              role="progressbar"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      <div className="row">
        {quiz.questions?.map((q, idx) => (
          <div key={q.id} className="col-12 mb-3">
            <div className="card shadow-sm border-0 rounded-4">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <span className="badge bg-dark rounded-pill mb-2">
                      Q{idx + 1}
                    </span>
                    <h5 className="fw-semibold mb-3">{q.question}</h5>
                  </div>

                  {answers[q.id] ? (
                    <span className="badge bg-success rounded-pill">
                      ✅ Answered
                    </span>
                  ) : (
                    <span className="badge bg-secondary rounded-pill">
                      ⏺ Not Answered
                    </span>
                  )}
                </div>

                <div className="row g-2">
                  {q.options.map((opt, index) => (
                    <div key={index} className="col-12 col-md-6">
                      <label
                        className={`border rounded-3 p-3 w-100 d-flex align-items-center gap-2 ${
                          answers[q.id] === opt
                            ? "border-primary bg-light"
                            : "border-light-subtle"
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          className="form-check-input mt-0"
                          type="radio"
                          name={`q-${q.id}`}
                          value={opt}
                          checked={answers[q.id] === opt}
                          onChange={() => handleOptionChange(q.id, opt)}
                        />
                        <span className="fw-medium">{opt}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card shadow-sm border-0 rounded-4 mt-4">
        <div className="card-body p-4 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
          <div>
            <h5 className="fw-bold mb-1">Ready to Submit?</h5>
            <p className="text-muted mb-0">
              You answered{" "}
              <span className="fw-semibold">
                {answeredCount}/{totalQuestions}
              </span>{" "}
              questions.
            </p>
          </div>

          <button
            className="btn btn-primary btn-lg px-4 rounded-3 fw-semibold"
            onClick={handleSubmit}
          >
            Submit Quiz ✅
          </button>
        </div>
      </div>
    </div>
  );
}
